import React, { useMemo, useState } from 'react';
import { Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import type { ClarifyingSlot, OrchestratorPipelineCandidate } from '../../../types/smartStart';

interface Props {
  pipeline: OrchestratorPipelineCandidate;
  initialValues: Record<string, string>;
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: (values: Record<string, string>) => void;
  submitLabel?: string;
}

const ClarifyingQuestionsForm: React.FC<Props> = ({
  pipeline,
  initialValues,
  isSubmitting,
  onBack,
  onSubmit,
  submitLabel,
}) => {
  const orderedSlots = useMemo<ClarifyingSlot[]>(() => {
    return Array.isArray(pipeline.clarifying_slots) ? pipeline.clarifying_slots : [];
  }, [pipeline]);

  const [values, setValues] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const slot of orderedSlots) {
      out[slot.key] = typeof initialValues[slot.key] === 'string' ? initialValues[slot.key] : '';
    }
    return out;
  });
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const missingRequired = useMemo(() => {
    return orderedSlots
      .filter((s) => s.required && !(values[s.key] && values[s.key].trim()))
      .map((s) => s.key);
  }, [orderedSlots, values]);

  const handleChange = (key: string, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  const handleSubmit = () => {
    setAttemptedSubmit(true);
    if (missingRequired.length > 0) return;
    const trimmed: Record<string, string> = {};
    for (const slot of orderedSlots) {
      const v = (values[slot.key] || '').trim();
      if (v) trimmed[slot.key] = v;
    }
    onSubmit(trimmed);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
            Clarify before we build the plan
          </div>
          <div className="text-base font-bold text-gray-900 mt-1">
            {pipeline.name}
          </div>
          {pipeline.description && (
            <p className="text-sm text-gray-600 mt-1">{pipeline.description}</p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {orderedSlots.map((slot) => {
          const isMissing = attemptedSubmit && slot.required && !(values[slot.key] && values[slot.key].trim());
          return (
            <div key={slot.key}>
              <label
                htmlFor={`slot-${slot.key}`}
                className="block text-sm font-semibold text-gray-900 mb-1"
              >
                {slot.label}
                {slot.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {slot.prompt && (
                <p className="text-xs text-gray-600 mb-2">{slot.prompt}</p>
              )}
              <textarea
                id={`slot-${slot.key}`}
                value={values[slot.key] || ''}
                onChange={(e) => handleChange(slot.key, e.target.value)}
                placeholder={slot.placeholder || ''}
                rows={2}
                className={`w-full p-3 rounded-xl border bg-white text-sm focus:ring-2 focus:ring-lavender/20 focus:outline-none ${
                  isMissing
                    ? 'border-red-300 focus:border-red-400'
                    : 'border-gray-300 focus:border-lavender'
                }`}
                disabled={isSubmitting}
              />
              {isMissing && (
                <div className="text-xs text-red-600 mt-1">
                  This is required. Leave it blank only if you intentionally want Jarvis to proceed without it — that decision will be logged in the audit trail.
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          disabled={isSubmitting}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to pipelines
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex items-center gap-2 px-5 py-2.5 bg-lavender text-white font-semibold rounded-xl hover:bg-lavender-deep transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Building plan…
            </>
          ) : (
            <>
              {submitLabel || 'Build my plan'}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ClarifyingQuestionsForm;
